import { useState, useEffect, useRef, useCallback } from 'react';
import API from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Settings, Users, Stethoscope, Shield, Moon, Sun, UserPlus, Image, Save, Upload, Loader2, Baby, Heart, Brain, Eye, Ear, Bone, Pill, Scissors, Activity, QrCode, Copy, ExternalLink, Droplet, FlaskConical, Radiation, AlertTriangle, FileText, Pencil, Trash2, HelpCircle, MessageCircle, Lock, Unlock, RotateCcw, MapPin, Navigation, Video, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import LocationPicker from '../components/LocationPicker';
import { DataImportExportSection } from '../components/DataImportExportSection';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/0e766822-208a-476d-a59e-994705ad0365/images/cec2e138381a5fb824ed97cd84e193e276d86f983cb673d7ab79196fb1da8923.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = typeof window !== 'undefined' ? window.location.origin : '';

// All Indian Medical Specialties
const ALL_SPECIALTY_PACKS = [
  // Broad Specialties (MD/MS)
  { id: 'general', name: 'General Physician (Internal Medicine)', desc: 'Standard intake forms, comprehensive SOAP templates, general examination', icon: Stethoscope, category: 'Broad Specialty', active: true },
  { id: 'anaesthesiology', name: 'Anaesthesiologist', desc: 'Pre-anesthesia assessment, ASA scoring, airway evaluation, anesthesia records', icon: Activity, category: 'Broad Specialty', active: false },
  { id: 'dermatology', name: 'Dermatologist', desc: 'Lesion documentation, skin imaging, SCORAD/PASI scoring, phototherapy tracking', icon: Image, category: 'Broad Specialty', active: false },
  { id: 'surgery', name: 'General Surgeon', desc: 'Pre-op assessment, surgical notes, wound tracking, post-op care plans', icon: Scissors, category: 'Broad Specialty', active: false },
  { id: 'gynecology', name: 'Gynaecologist', desc: 'Menstrual history, PAP smear tracking, HRT management, gynae procedures', icon: Heart, category: 'Broad Specialty', active: false },
  { id: 'obstetrics', name: 'Obstetrician', desc: 'LMP/EDD calculator, ANC visits, USG tracking, labor monitoring, delivery notes', icon: Baby, category: 'Broad Specialty', active: false },
  { id: 'ophthalmology', name: 'Ophthalmologist', desc: 'Visual acuity, IOP tracking, slit lamp findings, fundus documentation', icon: Eye, category: 'Broad Specialty', active: false },
  { id: 'orthopedics', name: 'Orthopaedic Surgeon', desc: 'Fracture tracking, joint examination, physiotherapy plans, implant records', icon: Bone, category: 'Broad Specialty', active: false },
  { id: 'ent', name: 'Otolaryngologist (ENT)', desc: 'Audiometry upload, voice assessment, nasal endoscopy, ENT examination', icon: Ear, category: 'Broad Specialty', active: false },
  { id: 'pediatrics', name: 'Paediatrician', desc: 'Growth charts (WHO/IAP), vaccination tracker, milestones, pediatric vitals', icon: Baby, category: 'Broad Specialty', active: false },
  { id: 'pathology', name: 'Pathologist', desc: 'Lab report templates, histopathology reporting, cytology documentation', icon: FlaskConical, category: 'Broad Specialty', active: false },
  { id: 'psychiatry', name: 'Psychiatrist', desc: 'Mental status exam, PHQ-9/GAD-7/HAM-D scoring, therapy notes, medication tracking', icon: Brain, category: 'Broad Specialty', active: false },
  { id: 'pulmonology', name: 'Pulmonologist (Chest Physician)', desc: 'PFT interpretation, oxygen therapy tracking, COPD/asthma scoring, chest imaging', icon: Activity, category: 'Broad Specialty', active: false },
  { id: 'radiology', name: 'Radiologist', desc: 'Imaging reports, BIRADS/TIRADS scoring, CT/MRI templates, PACS integration', icon: Radiation, category: 'Broad Specialty', active: false },
  
  // Super Specialties (DM/MCh)
  { id: 'cardiology', name: 'Cardiologist', desc: 'ECG upload, ECHO tracking, Framingham risk, NYHA class, cardiac cath reports', icon: Heart, category: 'Super Specialty', active: false },
  { id: 'cardiothoracic', name: 'Cardiothoracic Surgeon', desc: 'CABG/valve surgery notes, CPB records, ICU protocols, recovery tracking', icon: Scissors, category: 'Super Specialty', active: false },
  { id: 'endocrinology', name: 'Endocrinologist', desc: 'Diabetes management, HbA1c tracking, thyroid monitoring, hormone panels', icon: Activity, category: 'Super Specialty', active: false },
  { id: 'gastroenterology', name: 'Gastroenterologist', desc: 'Endoscopy reports, colonoscopy findings, liver function, IBD scoring', icon: Pill, category: 'Super Specialty', active: false },
  { id: 'haematology', name: 'Haematologist', desc: 'CBC trends, coagulation tracking, blood transfusion records, chemotherapy', icon: Droplet, category: 'Super Specialty', active: false },
  { id: 'medical_oncology', name: 'Medical Oncologist', desc: 'Chemotherapy protocols, tumor markers, staging, ECOG performance status', icon: Activity, category: 'Super Specialty', active: false },
  { id: 'neonatology', name: 'Neonatologist', desc: 'APGAR scoring, NICU monitoring, feeding charts, growth tracking', icon: Baby, category: 'Super Specialty', active: false },
  { id: 'nephrology', name: 'Nephrologist', desc: 'eGFR tracking, dialysis records, transplant follow-up, fluid balance', icon: Activity, category: 'Super Specialty', active: false },
  { id: 'neurology', name: 'Neurologist', desc: 'Neurological exam, NIHSS/GCS scoring, seizure diary, EEG/EMG reports', icon: Brain, category: 'Super Specialty', active: false },
  { id: 'neurosurgery', name: 'Neurosurgeon', desc: 'Craniotomy notes, spine surgery, shunt tracking, post-op neuro assessment', icon: Scissors, category: 'Super Specialty', active: false },
  { id: 'plastic_surgery', name: 'Plastic Surgeon', desc: 'Wound documentation, flap tracking, burn assessment, cosmetic procedures', icon: Scissors, category: 'Super Specialty', active: false },
  { id: 'surgical_oncology', name: 'Surgical Oncologist', desc: 'Tumor resection notes, staging, margins documentation, follow-up protocols', icon: Scissors, category: 'Super Specialty', active: false },
  { id: 'urology', name: 'Urologist', desc: 'Uroflowmetry, PSA tracking, stone analysis, cystoscopy reports', icon: Activity, category: 'Super Specialty', active: false },
  
  // Other Common Specialties
  { id: 'allergy', name: 'Allergist / Immunologist', desc: 'Allergy testing, immunotherapy protocols, anaphylaxis action plans', icon: AlertTriangle, category: 'Other Specialty', active: false },
  { id: 'diabetology', name: 'Diabetologist', desc: 'Glucose logs, insulin dosing, HbA1c trends, foot examination, diet plans', icon: Activity, category: 'Other Specialty', active: false },
  { id: 'forensic', name: 'Forensic Medicine Specialist', desc: 'Medico-legal documentation, injury reports, age estimation, autopsy records', icon: FileText, category: 'Other Specialty', active: false },
  { id: 'geriatrics', name: 'Geriatrician', desc: 'Comprehensive geriatric assessment, fall risk, cognitive screening, polypharmacy', icon: Activity, category: 'Other Specialty', active: false },
  { id: 'microbiology', name: 'Microbiologist', desc: 'Culture reports, sensitivity patterns, infection control, antibiogram', icon: FlaskConical, category: 'Other Specialty', active: false },
  { id: 'nuclear_medicine', name: 'Nuclear Medicine Physician', desc: 'PET/CT reports, thyroid scans, nuclear cardiology, radionuclide therapy', icon: Radiation, category: 'Other Specialty', active: false },
  { id: 'palliative', name: 'Palliative Care Specialist', desc: 'Symptom management, pain scales, advance directives, end-of-life planning', icon: Heart, category: 'Other Specialty', active: false },
  { id: 'rheumatology', name: 'Rheumatologist', desc: 'DAS28 scoring, joint examination, DMARDs tracking, autoimmune panels', icon: Bone, category: 'Other Specialty', active: false },
  { id: 'sports_medicine', name: 'Sports Medicine Physician', desc: 'Injury documentation, return-to-play protocols, performance tracking', icon: Activity, category: 'Other Specialty', active: false },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [darkMode, setDarkMode] = useState(false);
  const [users, setUsers] = useState([]);
  const [packs, setPacks] = useState(ALL_SPECIALTY_PACKS);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [userForm, setUserForm] = useState({ email: '', password: '', name: '', role: 'assistant', phone: '' });
  const [clinicSettings, setClinicSettings] = useState({
    clinic_name: '', clinic_address: '', clinic_phone: '', clinic_logo_url: '', gst_number: '', doctor_registration: '', tagline: 'Healthcare Simplified, Care Amplified.', consultation_fee: 500, clinic_timings: [],
    online_consultation_enabled: false, online_consultation_fee: 500, online_consultation_slots: [], clinic_upi_id: ''
  });
  const [savingClinic, setSavingClinic] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const [clinicCode, setClinicCode] = useState('');
  
  // Default clinic timings
  const defaultTimings = [
    { day: 'monday', is_open: true, morning_start: '09:00', morning_end: '13:00', evening_start: '17:00', evening_end: '21:00' },
    { day: 'tuesday', is_open: true, morning_start: '09:00', morning_end: '13:00', evening_start: '17:00', evening_end: '21:00' },
    { day: 'wednesday', is_open: true, morning_start: '09:00', morning_end: '13:00', evening_start: '17:00', evening_end: '21:00' },
    { day: 'thursday', is_open: true, morning_start: '09:00', morning_end: '13:00', evening_start: '17:00', evening_end: '21:00' },
    { day: 'friday', is_open: true, morning_start: '09:00', morning_end: '13:00', evening_start: '17:00', evening_end: '21:00' },
    { day: 'saturday', is_open: true, morning_start: '09:00', morning_end: '13:00', evening_start: '17:00', evening_end: '21:00' },
    { day: 'sunday', is_open: false, morning_start: '', morning_end: '', evening_start: '', evening_end: '' }
  ];
  const defaultOnlineSlots = [
    { day: 'monday', is_available: true, start_time: '10:00', end_time: '13:00' },
    { day: 'tuesday', is_available: true, start_time: '10:00', end_time: '13:00' },
    { day: 'wednesday', is_available: true, start_time: '10:00', end_time: '13:00' },
    { day: 'thursday', is_available: true, start_time: '10:00', end_time: '13:00' },
    { day: 'friday', is_available: true, start_time: '10:00', end_time: '13:00' },
    { day: 'saturday', is_available: false, start_time: '', end_time: '' },
    { day: 'sunday', is_available: false, start_time: '', end_time: '' }
  ];
  const [loadingCode, setLoadingCode] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', email: '', clinic_name: '', specialty: '', phone: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [editStaffOpen, setEditStaffOpen] = useState(false);
  const [editStaffForm, setEditStaffForm] = useState({ id: '', name: '', email: '', phone: '', role: '', password: '' });
  const [savingStaff, setSavingStaff] = useState(false);
  const [deleteStaffConfirm, setDeleteStaffConfirm] = useState(null);
  
  // Clinic Location State
  const [clinicLocation, setClinicLocation] = useState({
    latitude: null,
    longitude: null,
    address: ''
  });
  const [savingLocation, setSavingLocation] = useState(false);
  
  // Staff Permissions State
  const [staffPermissions, setStaffPermissions] = useState({
    receptionist_permissions: {},
    assistant_permissions: {}
  });
  const [featureDefinitions, setFeatureDefinitions] = useState([]);
  const [savingPermissions, setSavingPermissions] = useState(false);

  useEffect(() => {
    if (user?.role === 'doctor') {
      API.get('/auth/users').then(r => setUsers(r.data)).catch(console.error);
      API.get('/clinic-settings').then(r => {
        if (r.data) {
          setClinicSettings(prev => ({ 
            ...prev, 
            ...r.data,
            clinic_timings: r.data.clinic_timings || defaultTimings,
            online_consultation_slots: r.data.online_consultation_slots || defaultOnlineSlots
          }));
          // Load saved specialty packs
          if (r.data.specialty_packs) {
            setPacks(prev => prev.map(p => ({
              ...p,
              active: r.data.specialty_packs.includes(p.id)
            })));
          }
          if (r.data.clinic_code) {
            setClinicCode(r.data.clinic_code);
          }
        }
      }).catch(console.error);
      
      // Fetch clinic code
      API.get('/kiosk/clinic-code').then(r => {
        if (r.data.clinic_code) setClinicCode(r.data.clinic_code);
      }).catch(console.error);
      
      // Fetch staff permissions
      API.get('/staff-permissions/features').then(r => setFeatureDefinitions(r.data)).catch(console.error);
      API.get('/staff-permissions').then(r => {
        setStaffPermissions({
          receptionist_permissions: r.data.receptionist_permissions || {},
          assistant_permissions: r.data.assistant_permissions || {}
        });
      }).catch(console.error);
      
      // Fetch clinic location
      API.get('/auth/profile/location').then(r => {
        if (r.data) {
          setClinicLocation({
            latitude: r.data.latitude,
            longitude: r.data.longitude,
            address: r.data.address || ''
          });
        }
      }).catch(console.error);
    }
  }, [user]);

  useEffect(() => {
    if (darkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [darkMode]);

  // Handle location selection from LocationPicker
  const handleLocationSelect = useCallback(({ latitude, longitude, address }) => {
    setClinicLocation({ latitude, longitude, address });
  }, []);

  // Save clinic location
  const saveClinicLocation = async () => {
    if (!clinicLocation.latitude || !clinicLocation.longitude) {
      toast.error('Please select a location first');
      return;
    }
    setSavingLocation(true);
    try {
      await API.put('/auth/profile/location', {
        latitude: clinicLocation.latitude,
        longitude: clinicLocation.longitude,
        address: clinicLocation.address
      });
      toast.success('Clinic location saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save location');
    }
    setSavingLocation(false);
  };

  const togglePack = async (id) => {
    const updated = packs.map(p => p.id === id ? { ...p, active: !p.active } : p);
    setPacks(updated);
    
    // Save to backend
    try {
      const activePacks = updated.filter(p => p.active).map(p => p.id);
      await API.put('/clinic-settings', { ...clinicSettings, specialty_packs: activePacks });
      toast.success('Specialty pack updated');
    } catch (err) {
      console.error(err);
    }
  };

  const addStaffUser = async (e) => {
    e.preventDefault();
    try {
      await API.post('/auth/users', userForm);
      toast.success('Staff member added');
      setAddUserOpen(false);
      setUserForm({ email: '', password: '', name: '', role: 'assistant', phone: '' });
      const r = await API.get('/auth/users');
      setUsers(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openEditStaff = (staff) => {
    setEditStaffForm({
      id: staff.id,
      name: staff.name || '',
      email: staff.email || '',
      phone: staff.phone || '',
      role: staff.role || 'assistant',
      password: ''
    });
    setEditStaffOpen(true);
  };

  const handleUpdateStaff = async (e) => {
    e.preventDefault();
    setSavingStaff(true);
    try {
      const updateData = {
        name: editStaffForm.name,
        email: editStaffForm.email,
        phone: editStaffForm.phone,
        role: editStaffForm.role,
      };
      if (editStaffForm.password.trim()) {
        updateData.password = editStaffForm.password;
      }
      await API.put(`/auth/users/${editStaffForm.id}`, updateData);
      toast.success('Staff member updated');
      setEditStaffOpen(false);
      const r = await API.get('/auth/users');
      setUsers(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingStaff(false);
    }
  };

  const handleDeleteStaff = async (staffId) => {
    try {
      await API.delete(`/auth/users/${staffId}`);
      toast.success('Staff member deleted');
      setDeleteStaffConfirm(null);
      const r = await API.get('/auth/users');
      setUsers(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // Staff Permissions Functions
  const togglePermission = (role, featureId) => {
    const key = `${role}_permissions`;
    setStaffPermissions(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [featureId]: !prev[key][featureId]
      }
    }));
  };

  const saveStaffPermissions = async () => {
    setSavingPermissions(true);
    try {
      await API.put('/staff-permissions', staffPermissions);
      toast.success('Staff permissions saved successfully');
    } catch (err) {
      toast.error('Failed to save permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const resetPermissionsToDefaults = async () => {
    setSavingPermissions(true);
    try {
      const res = await API.post('/staff-permissions/reset-to-defaults');
      setStaffPermissions({
        receptionist_permissions: res.data.receptionist_permissions,
        assistant_permissions: res.data.assistant_permissions
      });
      toast.success('Permissions reset to defaults');
    } catch (err) {
      toast.error('Failed to reset permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload an image (JPG, PNG, WebP, GIF) or PDF');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${BACKEND_URL}/api/upload/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('clinic_token')}` },
        body: formData
      });
      
      if (!res.ok) throw new Error('Upload failed');
      
      const data = await res.json();
      setClinicSettings(prev => ({ ...prev, clinic_logo_url: data.url }));
      toast.success('Logo uploaded successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const saveClinicSettings = async () => {
    setSavingClinic(true);
    try {
      const activePacks = packs.filter(p => p.active).map(p => p.id);
      const res = await API.put('/clinic-settings', { ...clinicSettings, specialty_packs: activePacks });
      setClinicSettings(prev => ({ ...prev, ...res.data }));
      // Also update online consultation enabled flag on user doc
      try {
        await API.put('/patient-consultation/doctor/settings?enabled=' + (clinicSettings.online_consultation_enabled ? 'true' : 'false') + '&fee=' + (clinicSettings.online_consultation_fee || 500), null);
      } catch {}
      toast.success('Clinic settings saved');
    } catch (err) { toast.error('Failed to save settings'); }
    finally { setSavingClinic(false); }
  };

  const openEditProfile = () => {
    setProfileForm({
      name: user?.name || '',
      email: user?.email || '',
      clinic_name: user?.clinic_name || '',
      specialty: user?.specialty || '',
      phone: user?.phone || ''
    });
    setEditProfileOpen(true);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await API.put('/auth/profile', profileForm);
      toast.success('Profile updated successfully');
      setEditProfileOpen(false);
      // Refresh page to get updated user data
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div data-testid="settings-page" className="space-y-6 max-w-3xl overflow-hidden">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{fontFamily:'Manrope'}}>Settings</h1>
        <p className="text-sm text-slate-500">Manage your clinic configuration</p>

      <DataImportExportSection />

      </div>

      {/* Clinic Branding & Logo */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}>
            <Image className="w-5 h-5 text-teal-600" /> Clinic Branding & Invoice Settings
          </CardTitle>
          <CardDescription>Customize your clinic identity shown on invoices and documents</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Logo Preview & Upload */}
          <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <img
              src={clinicSettings.clinic_logo_url || LOGO_URL}
              alt="Clinic Logo"
              className="w-16 h-16 rounded-xl object-contain border border-slate-200 dark:border-slate-700 bg-white p-1"
              data-testid="clinic-logo-preview"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-900 dark:text-white">Clinic Logo</p>
              <p className="text-xs text-slate-400 mt-0.5">Upload an image (JPG, PNG, WebP) or PDF. Max 5MB.</p>
              <input
                type="file"
                ref={logoInputRef}
                onChange={handleLogoUpload}
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                className="hidden"
              />
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2" 
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                data-testid="upload-logo-btn"
              >
                {uploadingLogo ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
              </Button>
            </div>
          </div>
          <div>
            <Label>Logo URL (or paste external URL)</Label>
            <Input
              data-testid="clinic-logo-url-input"
              value={clinicSettings.clinic_logo_url}
              onChange={e => setClinicSettings({...clinicSettings, clinic_logo_url: e.target.value})}
              placeholder="https://your-logo-url.com/logo.png"
              className="h-12 mt-1"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Clinic Name</Label>
              <Input data-testid="clinic-name-input" value={clinicSettings.clinic_name} onChange={e => setClinicSettings({...clinicSettings, clinic_name: e.target.value})} className="h-12 mt-1" placeholder="City Medical Center" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="clinic-phone-input" value={clinicSettings.clinic_phone} onChange={e => setClinicSettings({...clinicSettings, clinic_phone: e.target.value})} className="h-12 mt-1" placeholder="+91 98765 43210" />
            </div>
          </div>
          <div>
            <Label>Clinic Address</Label>
            <Textarea data-testid="clinic-address-input" value={clinicSettings.clinic_address} onChange={e => setClinicSettings({...clinicSettings, clinic_address: e.target.value})} className="mt-1" placeholder="123 Medical Lane, City, State - PIN" />
          </div>
          
          <div>
            <Label>Consultation Fee (₹)</Label>
            <Input
              data-testid="consultation-fee-input"
              type="number"
              min="0"
              value={clinicSettings.consultation_fee || ''}
              onChange={e => setClinicSettings({...clinicSettings, consultation_fee: parseFloat(e.target.value) || 0})}
              className="h-12 mt-1"
              placeholder="Enter consultation charges"
            />
            <p className="text-xs text-muted-foreground mt-1">This fee will auto-populate in billing invoices and show to patients during appointment booking.</p>
          </div>

          <div>
            <Label>Clinic UPI ID (for patient Instant UPI Pay)</Label>
            <Input
              data-testid="clinic-upi-id-input"
              value={clinicSettings.clinic_upi_id || ''}
              onChange={e => setClinicSettings({...clinicSettings, clinic_upi_id: e.target.value})}
              className="h-12 mt-1"
              placeholder="yourclinic@upi"
            />
            <p className="text-xs text-muted-foreground mt-1">Patients will see an "Instant UPI Pay" button on My Bills that opens their UPI app directly to this ID. Mark bills paid manually after you receive the payment.</p>
          </div>
          
          {/* Clinic GPS Location for MR Check-in Verification */}
          <Card className="border-teal-200 bg-teal-50/30">
            <CardContent className="p-4">
              <LocationPicker
                onLocationSelect={handleLocationSelect}
                initialLatitude={clinicLocation.latitude}
                initialLongitude={clinicLocation.longitude}
                initialAddress={clinicLocation.address}
              />
              <div className="mt-3 flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  This location is used to verify MR visits via the kiosk QR check-in
                </p>
                <Button 
                  size="sm" 
                  onClick={saveClinicLocation} 
                  disabled={savingLocation || !clinicLocation.latitude}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {savingLocation ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Location
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>GST Number</Label>
              <Input data-testid="clinic-gst-input" value={clinicSettings.gst_number} onChange={e => setClinicSettings({...clinicSettings, gst_number: e.target.value})} className="h-12 mt-1" placeholder="GSTIN" />
            </div>
            <div>
              <Label>Doctor Registration No.</Label>
              <Input data-testid="clinic-reg-input" value={clinicSettings.doctor_registration} onChange={e => setClinicSettings({...clinicSettings, doctor_registration: e.target.value})} className="h-12 mt-1" placeholder="MCI/State Reg. No." />
            </div>
          </div>
          <div>
            <Label>Tagline (shown on invoice footer)</Label>
            <Input data-testid="clinic-tagline-input" value={clinicSettings.tagline} onChange={e => setClinicSettings({...clinicSettings, tagline: e.target.value})} className="h-12 mt-1" placeholder="Healthcare Simplified, Care Amplified." />
          </div>
          <Button className="bg-teal-700 hover:bg-teal-800 text-white h-11" onClick={saveClinicSettings} disabled={savingClinic} data-testid="clinic-settings-save-btn">
            <Save className="w-4 h-4 mr-2" /> {savingClinic ? 'Saving...' : 'Save Clinic Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* Clinic Timings */}
      <Card className="border-slate-200 dark:border-slate-800" data-testid="clinic-timings-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}>
            <Settings className="w-5 h-5 text-blue-600" /> Clinic Timings
          </CardTitle>
          <CardDescription>Set your clinic's working hours for each day of the week. This will be shown to patients when booking appointments.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(clinicSettings.clinic_timings || defaultTimings).map((timing, index) => (
            <div key={timing.day} className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-24">
                  <Label className="capitalize font-semibold">{timing.day}</Label>
                </div>
                <Switch
                  checked={timing.is_open}
                  onCheckedChange={(checked) => {
                    const newTimings = [...(clinicSettings.clinic_timings || defaultTimings)];
                    newTimings[index] = { ...newTimings[index], is_open: checked };
                    setClinicSettings({ ...clinicSettings, clinic_timings: newTimings });
                  }}
                />
                <span className={`text-sm ${timing.is_open ? 'text-green-600' : 'text-red-500'}`}>
                  {timing.is_open ? 'Open' : 'Closed'}
                </span>
              </div>
              {timing.is_open && (
                <div className="space-y-2 pl-0 sm:pl-6">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500 w-16 flex-shrink-0">Morning:</Label>
                    <Input type="time" className="flex-1 min-w-0 h-9" value={timing.morning_start || ''}
                      onChange={(e) => { const t = [...(clinicSettings.clinic_timings || defaultTimings)]; t[index] = { ...t[index], morning_start: e.target.value }; setClinicSettings({ ...clinicSettings, clinic_timings: t }); }} />
                    <span className="text-slate-400">-</span>
                    <Input type="time" className="flex-1 min-w-0 h-9" value={timing.morning_end || ''}
                      onChange={(e) => { const t = [...(clinicSettings.clinic_timings || defaultTimings)]; t[index] = { ...t[index], morning_end: e.target.value }; setClinicSettings({ ...clinicSettings, clinic_timings: t }); }} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500 w-16 flex-shrink-0">Evening:</Label>
                    <Input type="time" className="flex-1 min-w-0 h-9" value={timing.evening_start || ''}
                      onChange={(e) => { const t = [...(clinicSettings.clinic_timings || defaultTimings)]; t[index] = { ...t[index], evening_start: e.target.value }; setClinicSettings({ ...clinicSettings, clinic_timings: t }); }} />
                    <span className="text-slate-400">-</span>
                    <Input type="time" className="flex-1 min-w-0 h-9" value={timing.evening_end || ''}
                      onChange={(e) => { const t = [...(clinicSettings.clinic_timings || defaultTimings)]; t[index] = { ...t[index], evening_end: e.target.value }; setClinicSettings({ ...clinicSettings, clinic_timings: t }); }} />
                  </div>
                  {(timing.extra_slots || []).map((slot, si) => (
                    <div key={si} className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500 w-16 flex-shrink-0">Slot {si + 3}:</Label>
                      <Input type="time" className="flex-1 min-w-0 h-9" value={slot.start || ''}
                        onChange={(e) => { const t = [...(clinicSettings.clinic_timings || defaultTimings)]; const es = [...(t[index].extra_slots || [])]; es[si] = { ...es[si], start: e.target.value }; t[index] = { ...t[index], extra_slots: es }; setClinicSettings({ ...clinicSettings, clinic_timings: t }); }} />
                      <span className="text-slate-400">-</span>
                      <Input type="time" className="flex-1 min-w-0 h-9" value={slot.end || ''}
                        onChange={(e) => { const t = [...(clinicSettings.clinic_timings || defaultTimings)]; const es = [...(t[index].extra_slots || [])]; es[si] = { ...es[si], end: e.target.value }; t[index] = { ...t[index], extra_slots: es }; setClinicSettings({ ...clinicSettings, clinic_timings: t }); }} />
                      <button className="text-red-500 hover:text-red-700 p-1" data-testid={`remove-clinic-slot-${index}-${si}`}
                        onClick={() => { const t = [...(clinicSettings.clinic_timings || defaultTimings)]; const es = [...(t[index].extra_slots || [])]; es.splice(si, 1); t[index] = { ...t[index], extra_slots: es }; setClinicSettings({ ...clinicSettings, clinic_timings: t }); }}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1 mt-1" data-testid={`add-clinic-slot-${index}`}
                    onClick={() => { const t = [...(clinicSettings.clinic_timings || defaultTimings)]; const es = [...(t[index].extra_slots || []), { start: '', end: '' }]; t[index] = { ...t[index], extra_slots: es }; setClinicSettings({ ...clinicSettings, clinic_timings: t }); }}>
                    <Plus className="w-3 h-3" /> Add Time Slot
                  </button>
                </div>
              )}
            </div>
          ))}
          <Button 
            className="bg-blue-600 hover:bg-blue-700 text-white h-11 mt-4" 
            onClick={saveClinicSettings} 
            disabled={savingClinic}
          >
            <Save className="w-4 h-4 mr-2" /> {savingClinic ? 'Saving...' : 'Save Clinic Timings'}
          </Button>
        </CardContent>
      </Card>

      {/* Online Consultation Settings */}
      <Card className="border-slate-200 dark:border-slate-800" data-testid="online-consultation-settings-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}>
            <Video className="w-5 h-5 text-blue-600" /> Online Consultation Settings
          </CardTitle>
          <CardDescription>Configure your availability for online video consultations with patients. This is separate from your in-clinic timings.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <div>
              <Label className="font-semibold">Enable Online Consultations</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Allow patients to book video consultations with you</p>
            </div>
            <Switch
              data-testid="online-consult-toggle"
              checked={clinicSettings.online_consultation_enabled || false}
              onCheckedChange={(checked) => setClinicSettings(s => ({ ...s, online_consultation_enabled: checked }))}
            />
          </div>
          {clinicSettings.online_consultation_enabled && (
            <>
              <div>
                <Label>Online Consultation Fee (₹)</Label>
                <Input
                  data-testid="online-consultation-fee-input"
                  type="number" min="0"
                  value={clinicSettings.online_consultation_fee || ''}
                  onChange={e => setClinicSettings(s => ({ ...s, online_consultation_fee: parseFloat(e.target.value) || 0 }))}
                  className="h-12 mt-1 max-w-xs"
                  placeholder="Enter online consultation fee"
                />
                <p className="text-xs text-muted-foreground mt-1">This fee is shown to patients and charged upon appointment confirmation. Can differ from your in-clinic fee.</p>
              </div>
              <div className="space-y-3">
                <Label>Online Availability Schedule</Label>
                {(clinicSettings.online_consultation_slots || defaultOnlineSlots).map((slot, index) => (
                  <div key={slot.day} className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-24">
                        <Label className="capitalize font-semibold">{slot.day}</Label>
                      </div>
                      <Switch
                        checked={slot.is_available}
                        onCheckedChange={(checked) => {
                          const newSlots = [...(clinicSettings.online_consultation_slots || defaultOnlineSlots)];
                          newSlots[index] = { ...newSlots[index], is_available: checked };
                          setClinicSettings(s => ({ ...s, online_consultation_slots: newSlots }));
                        }}
                      />
                      <span className={`text-sm ${slot.is_available ? 'text-green-600' : 'text-red-500'}`}>
                        {slot.is_available ? 'Available' : 'Unavailable'}
                      </span>
                    </div>
                    {slot.is_available && (
                      <div className="space-y-2 pl-0 sm:pl-6">
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-slate-500 w-16 flex-shrink-0">Slot 1:</Label>
                          <Input type="time" className="flex-1 min-w-0 h-9"
                            value={slot.start_time || ''}
                            onChange={(e) => {
                              const newSlots = [...(clinicSettings.online_consultation_slots || defaultOnlineSlots)];
                              newSlots[index] = { ...newSlots[index], start_time: e.target.value };
                              setClinicSettings(s => ({ ...s, online_consultation_slots: newSlots }));
                            }}
                          />
                          <span className="text-slate-400 flex-shrink-0">to</span>
                          <Input type="time" className="flex-1 min-w-0 h-9"
                            value={slot.end_time || ''}
                            onChange={(e) => {
                              const newSlots = [...(clinicSettings.online_consultation_slots || defaultOnlineSlots)];
                              newSlots[index] = { ...newSlots[index], end_time: e.target.value };
                              setClinicSettings(s => ({ ...s, online_consultation_slots: newSlots }));
                            }}
                          />
                        </div>
                        {(slot.extra_slots || []).map((es, si) => (
                          <div key={si} className="flex items-center gap-2">
                            <Label className="text-xs text-slate-500 w-16 flex-shrink-0">Slot {si + 2}:</Label>
                            <Input type="time" className="flex-1 min-w-0 h-9" value={es.start || ''}
                              onChange={(e) => { const ns = [...(clinicSettings.online_consultation_slots || defaultOnlineSlots)]; const exs = [...(ns[index].extra_slots || [])]; exs[si] = { ...exs[si], start: e.target.value }; ns[index] = { ...ns[index], extra_slots: exs }; setClinicSettings(s => ({ ...s, online_consultation_slots: ns })); }} />
                            <span className="text-slate-400 flex-shrink-0">to</span>
                            <Input type="time" className="flex-1 min-w-0 h-9" value={es.end || ''}
                              onChange={(e) => { const ns = [...(clinicSettings.online_consultation_slots || defaultOnlineSlots)]; const exs = [...(ns[index].extra_slots || [])]; exs[si] = { ...exs[si], end: e.target.value }; ns[index] = { ...ns[index], extra_slots: exs }; setClinicSettings(s => ({ ...s, online_consultation_slots: ns })); }} />
                            <button className="text-red-500 hover:text-red-700 p-1" data-testid={`remove-online-slot-${index}-${si}`}
                              onClick={() => { const ns = [...(clinicSettings.online_consultation_slots || defaultOnlineSlots)]; const exs = [...(ns[index].extra_slots || [])]; exs.splice(si, 1); ns[index] = { ...ns[index], extra_slots: exs }; setClinicSettings(s => ({ ...s, online_consultation_slots: ns })); }}>
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1 mt-1" data-testid={`add-online-slot-${index}`}
                          onClick={() => { const ns = [...(clinicSettings.online_consultation_slots || defaultOnlineSlots)]; const exs = [...(ns[index].extra_slots || []), { start: '', end: '' }]; ns[index] = { ...ns[index], extra_slots: exs }; setClinicSettings(s => ({ ...s, online_consultation_slots: ns })); }}>
                          <Plus className="w-3 h-3" /> Add Time Slot
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white h-11 mt-4"
                onClick={saveClinicSettings}
                disabled={savingClinic}
                data-testid="save-online-consultation-settings-btn"
              >
                <Save className="w-4 h-4 mr-2" /> {savingClinic ? 'Saving...' : 'Save Online Consultation Settings'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Clinic-Wide QR Code for Self Check-In */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}>
            <QrCode className="w-5 h-5 text-teal-600" /> Self Check-In QR Code
          </CardTitle>
          <CardDescription>Single QR code for all patients to check-in using their phone number</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <div className="bg-white p-4 rounded-xl border border-slate-200 flex-shrink-0">
              {clinicCode ? (
                <QRCodeSVG 
                  value={`${FRONTEND_URL}/checkin/${clinicCode}`}
                  size={150}
                  level="H"
                  includeMargin={true}
                />
              ) : (
                <div className="w-[150px] h-[150px] flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 w-full space-y-3">
              <div>
                <Label className="text-xs text-slate-400">Clinic Code</Label>
                <p className="text-xl font-bold text-teal-700 break-all" data-testid="clinic-code">{clinicCode || '...'}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Check-In URL</Label>
                <p className="text-sm text-slate-600 font-mono bg-slate-50 p-2 rounded break-all">
                  {clinicCode ? `${FRONTEND_URL}/checkin/${clinicCode}` : 'Loading...'}
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    navigator.clipboard.writeText(`${FRONTEND_URL}/checkin/${clinicCode}`);
                    toast.success('Link copied to clipboard');
                  }}
                  disabled={!clinicCode}
                  data-testid="copy-checkin-link"
                >
                  <Copy className="w-4 h-4 mr-1" /> Copy Link
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => window.open(`${FRONTEND_URL}/checkin/${clinicCode}`, '_blank')}
                  disabled={!clinicCode}
                  data-testid="open-checkin-link"
                >
                  <ExternalLink className="w-4 h-4 mr-1" /> Preview
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Print this QR code and display at your clinic entrance. Patients can scan to check-in, enter symptoms, and get a token number. Their data will appear in the Clinical SOAP form.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Doctor Profile */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}><Stethoscope className="w-5 h-5 text-teal-600" /> Doctor Profile</CardTitle>
            <Button variant="outline" size="sm" onClick={openEditProfile} data-testid="edit-profile-btn">
              <Settings className="w-4 h-4 mr-1" /> Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: 'Doctor Name', value: user?.name },
              { label: 'Email', value: user?.email },
              { label: 'Phone', value: user?.phone || 'Not set' },
              { label: 'Clinic', value: user?.clinic_name || 'Not set' },
              { label: 'Specialty', value: user?.specialty || 'General Practice' },
            ].map(item => (
              <div key={item.label}>
                <Label className="text-xs text-slate-400">{item.label}</Label>
                <p className="text-sm font-medium text-slate-900 dark:text-white">{item.value}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit Profile Dialog */}
      <Dialog open={editProfileOpen} onOpenChange={setEditProfileOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Manrope'}}>Edit Doctor Profile</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input data-testid="edit-profile-name" value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})} className="h-12 mt-1" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="edit-profile-email" type="email" value={profileForm.email} onChange={e => setProfileForm({...profileForm, email: e.target.value})} className="h-12 mt-1" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="edit-profile-phone" value={profileForm.phone} onChange={e => setProfileForm({...profileForm, phone: e.target.value})} className="h-12 mt-1" placeholder="+91 98765 43210" />
            </div>
            <div>
              <Label>Clinic Name</Label>
              <Input data-testid="edit-profile-clinic" value={profileForm.clinic_name} onChange={e => setProfileForm({...profileForm, clinic_name: e.target.value})} className="h-12 mt-1" />
            </div>
            <div>
              <Label>Specialty</Label>
              <Select value={profileForm.specialty} onValueChange={v => setProfileForm({...profileForm, specialty: v})}>
                <SelectTrigger className="h-12 mt-1" data-testid="edit-profile-specialty">
                  <SelectValue placeholder="Select specialty" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="General Physician">General Physician (Internal Medicine)</SelectItem>
                  <SelectItem value="Anaesthesiologist">Anaesthesiologist</SelectItem>
                  <SelectItem value="Dermatologist">Dermatologist</SelectItem>
                  <SelectItem value="General Surgeon">General Surgeon</SelectItem>
                  <SelectItem value="Gynaecologist">Gynaecologist</SelectItem>
                  <SelectItem value="Obstetrician">Obstetrician</SelectItem>
                  <SelectItem value="Ophthalmologist">Ophthalmologist</SelectItem>
                  <SelectItem value="Orthopaedic Surgeon">Orthopaedic Surgeon</SelectItem>
                  <SelectItem value="ENT Specialist">Otolaryngologist (ENT)</SelectItem>
                  <SelectItem value="Paediatrician">Paediatrician</SelectItem>
                  <SelectItem value="Pathologist">Pathologist</SelectItem>
                  <SelectItem value="Psychiatrist">Psychiatrist</SelectItem>
                  <SelectItem value="Pulmonologist">Pulmonologist (Chest Physician)</SelectItem>
                  <SelectItem value="Radiologist">Radiologist</SelectItem>
                  <SelectItem value="Cardiologist">Cardiologist</SelectItem>
                  <SelectItem value="Cardiothoracic Surgeon">Cardiothoracic Surgeon</SelectItem>
                  <SelectItem value="Endocrinologist">Endocrinologist</SelectItem>
                  <SelectItem value="Gastroenterologist">Gastroenterologist</SelectItem>
                  <SelectItem value="Haematologist">Haematologist</SelectItem>
                  <SelectItem value="Medical Oncologist">Medical Oncologist</SelectItem>
                  <SelectItem value="Neonatologist">Neonatologist</SelectItem>
                  <SelectItem value="Nephrologist">Nephrologist</SelectItem>
                  <SelectItem value="Neurologist">Neurologist</SelectItem>
                  <SelectItem value="Neurosurgeon">Neurosurgeon</SelectItem>
                  <SelectItem value="Plastic Surgeon">Plastic Surgeon</SelectItem>
                  <SelectItem value="Surgical Oncologist">Surgical Oncologist</SelectItem>
                  <SelectItem value="Urologist">Urologist</SelectItem>
                  <SelectItem value="Allergist">Allergist / Immunologist</SelectItem>
                  <SelectItem value="Diabetologist">Diabetologist</SelectItem>
                  <SelectItem value="Forensic Medicine">Forensic Medicine Specialist</SelectItem>
                  <SelectItem value="Geriatrician">Geriatrician</SelectItem>
                  <SelectItem value="Microbiologist">Microbiologist</SelectItem>
                  <SelectItem value="Nuclear Medicine">Nuclear Medicine Physician</SelectItem>
                  <SelectItem value="Palliative Care">Palliative Care Specialist</SelectItem>
                  <SelectItem value="Rheumatologist">Rheumatologist</SelectItem>
                  <SelectItem value="Sports Medicine">Sports Medicine Physician</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white" disabled={savingProfile} data-testid="edit-profile-submit-btn">
              {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {savingProfile ? 'Saving...' : 'Save Profile'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Appearance */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader><CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Appearance</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {darkMode ? <Moon className="w-5 h-5 text-slate-400" /> : <Sun className="w-5 h-5 text-amber-500" />}
              <div><p className="text-sm font-medium">Dark Mode</p><p className="text-xs text-slate-400">Switch between light and dark theme</p></div>
            </div>
            <Switch data-testid="settings-dark-mode-toggle" checked={darkMode} onCheckedChange={setDarkMode} />
          </div>
        </CardContent>
      </Card>

      {/* Staff Management */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}><Users className="w-5 h-5 text-blue-600" /> Staff</CardTitle>
            <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
              <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="settings-add-staff-btn"><UserPlus className="w-4 h-4 mr-1" /> Add Staff</Button></DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle style={{fontFamily:'Manrope'}}>Add Staff Member</DialogTitle></DialogHeader>
                <form onSubmit={addStaffUser} className="space-y-4">
                  <div><Label>Name</Label><Input data-testid="staff-name-input" value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})} className="h-12 mt-1" required /></div>
                  <div><Label>Email</Label><Input data-testid="staff-email-input" type="email" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} className="h-12 mt-1" required /></div>
                  <div><Label>Password</Label><Input data-testid="staff-password-input" type="password" value={userForm.password} onChange={e => setUserForm({...userForm, password: e.target.value})} className="h-12 mt-1" required /></div>
                  <div><Label>Phone</Label><Input data-testid="staff-phone-input" value={userForm.phone} onChange={e => setUserForm({...userForm, phone: e.target.value})} className="h-12 mt-1" placeholder="+91 98765 43210" /></div>
                  <div><Label>Role</Label>
                    <Select value={userForm.role} onValueChange={v => setUserForm({...userForm, role: v})}>
                      <SelectTrigger className="h-12 mt-1" data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="assistant">Assistant / Compounder</SelectItem><SelectItem value="receptionist">Receptionist</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white" data-testid="staff-submit-btn">Add Staff Member</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="space-y-2" data-testid="settings-staff-list">
          {users.filter(u => u.role !== 'doctor').map(u => (
            <div key={u.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                  <span className="text-sm font-bold text-teal-700 dark:text-teal-400">{u.name?.charAt(0)}</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">{u.name}</p>
                  <p className="text-xs text-slate-400">{u.email} {u.phone && `• ${u.phone}`}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="capitalize text-xs">{u.role}</Badge>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditStaff(u)} data-testid={`edit-staff-${u.id}`}>
                  <Pencil className="w-4 h-4 text-slate-500 hover:text-teal-600" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDeleteStaffConfirm(u.id)} data-testid={`delete-staff-${u.id}`}>
                  <Trash2 className="w-4 h-4 text-slate-500 hover:text-red-600" />
                </Button>
              </div>
            </div>
          ))}
          {users.filter(u => u.role !== 'doctor').length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">No staff members added yet</p>
          )}
        </CardContent>
      </Card>

      {/* Staff Permissions */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <div className="space-y-3">
            <div>
              <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}>
                <Shield className="w-5 h-5 text-purple-600" /> Staff Permissions
              </CardTitle>
              <CardDescription>Control which features are accessible to your staff</CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={resetPermissionsToDefaults} disabled={savingPermissions} data-testid="reset-permissions-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Reset to Defaults
              </Button>
              <Button size="sm" onClick={saveStaffPermissions} disabled={savingPermissions} data-testid="save-permissions-btn">
                {savingPermissions ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Receptionist Permissions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-2 border-b">
                <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Users className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 dark:text-white">Receptionist</h4>
                  <p className="text-xs text-slate-500">Front desk and patient management</p>
                </div>
              </div>
              <div className="space-y-2">
                {featureDefinitions.map(feature => (
                  <div 
                    key={`receptionist-${feature.id}`} 
                    className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    <div className="flex-1 pr-4">
                      <p className="text-sm font-medium text-slate-800 dark:text-white">{feature.name}</p>
                      <p className="text-xs text-slate-500 line-clamp-1">{feature.description}</p>
                    </div>
                    <Switch
                      checked={staffPermissions.receptionist_permissions?.[feature.id] ?? false}
                      onCheckedChange={() => togglePermission('receptionist', feature.id)}
                      data-testid={`perm-receptionist-${feature.id}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Assistant Permissions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-2 border-b">
                <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Stethoscope className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 dark:text-white">Assistant / Compounder</h4>
                  <p className="text-xs text-slate-500">Clinical support and patient care</p>
                </div>
              </div>
              <div className="space-y-2">
                {featureDefinitions.map(feature => (
                  <div 
                    key={`assistant-${feature.id}`} 
                    className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    <div className="flex-1 pr-4">
                      <p className="text-sm font-medium text-slate-800 dark:text-white">{feature.name}</p>
                      <p className="text-xs text-slate-500 line-clamp-1">{feature.description}</p>
                    </div>
                    <Switch
                      checked={staffPermissions.assistant_permissions?.[feature.id] ?? false}
                      onCheckedChange={() => togglePermission('assistant', feature.id)}
                      data-testid={`perm-assistant-${feature.id}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Legend */}
          <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-purple-600 mt-0.5" />
              <div>
                <h5 className="font-medium text-slate-800 dark:text-white text-sm">How Permissions Work</h5>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                  Toggle features ON/OFF for each role. When a feature is OFF, it won't appear in the staff member's navigation menu.
                  Staff members will only see and access the features you enable for their role.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Staff Dialog */}
      <Dialog open={editStaffOpen} onOpenChange={setEditStaffOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{fontFamily:'Manrope'}}>Edit Staff Member</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdateStaff} className="space-y-4">
            <div><Label>Name</Label><Input data-testid="edit-staff-name" value={editStaffForm.name} onChange={e => setEditStaffForm({...editStaffForm, name: e.target.value})} className="h-12 mt-1" required /></div>
            <div><Label>Email</Label><Input data-testid="edit-staff-email" type="email" value={editStaffForm.email} onChange={e => setEditStaffForm({...editStaffForm, email: e.target.value})} className="h-12 mt-1" required /></div>
            <div><Label>Phone</Label><Input data-testid="edit-staff-phone" value={editStaffForm.phone} onChange={e => setEditStaffForm({...editStaffForm, phone: e.target.value})} className="h-12 mt-1" placeholder="+91 98765 43210" /></div>
            <div><Label>Role</Label>
              <Select value={editStaffForm.role} onValueChange={v => setEditStaffForm({...editStaffForm, role: v})}>
                <SelectTrigger className="h-12 mt-1" data-testid="edit-staff-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="assistant">Assistant / Compounder</SelectItem><SelectItem value="receptionist">Receptionist</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>New Password (leave blank to keep current)</Label><Input data-testid="edit-staff-password" type="password" value={editStaffForm.password} onChange={e => setEditStaffForm({...editStaffForm, password: e.target.value})} className="h-12 mt-1" placeholder="Enter new password" /></div>
            <Button type="submit" className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white" disabled={savingStaff} data-testid="edit-staff-submit-btn">
              {savingStaff ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {savingStaff ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Staff Confirmation Dialog */}
      <Dialog open={!!deleteStaffConfirm} onOpenChange={() => setDeleteStaffConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle style={{fontFamily:'Manrope'}}>Delete Staff Member?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Are you sure you want to delete this staff member? This action cannot be undone.</p>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setDeleteStaffConfirm(null)}>Cancel</Button>
            <Button variant="destructive" className="flex-1" onClick={() => handleDeleteStaff(deleteStaffConfirm)} data-testid="confirm-delete-staff">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Specialty Packs - All Indian Specialties */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}><Shield className="w-5 h-5 text-purple-600" /> Specialty Packs</CardTitle>
          <CardDescription>Enable modular specialty features for your clinic. Each pack adds specialty-specific templates, vitals, and tools. 35+ Indian medical specialties available.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6" data-testid="settings-specialty-packs">
          {/* Group by category */}
          {['Broad Specialty', 'Super Specialty', 'Other Specialty'].map(category => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">{category === 'Broad Specialty' ? 'MD/MS' : category === 'Super Specialty' ? 'DM/MCh' : 'Other'}</Badge>
                {category}
              </h3>
              <div className="space-y-2">
                {packs.filter(p => p.category === category).map(pack => {
                  const IconComponent = pack.icon;
                  return (
                    <div key={pack.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${pack.active ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-500'}`}>
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{pack.name}</p>
                          <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">{pack.desc}</p>
                        </div>
                      </div>
                      <Switch data-testid={`pack-toggle-${pack.id}`} checked={pack.active} onCheckedChange={() => togglePack(pack.id)} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Security */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}><Shield className="w-5 h-5 text-emerald-600" /> Security & Compliance</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { label: 'Encryption at Rest', status: 'AES-256' },
              { label: 'TLS in Transit', status: 'TLS 1.3' },
              { label: 'ABDM Integration', status: 'Ready' },
              { label: 'DPDP Compliance', status: 'Structure Ready' },
              { label: 'Audit Logging', status: 'Enabled' },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                <span className="text-sm text-slate-700 dark:text-slate-300">{item.label}</span>
                <Badge className="bg-emerald-100 text-emerald-800 text-xs">{item.status}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
